import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import MicIcon from '@assets/icons/mic.svg';
import React, { useCallback, useRef, useState } from 'react';
import { ActionSheetContent } from '@/components/action-sheet';
import { Button } from '@/components/button';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { ItemDetailContextProvider } from '@/contexts/item-detail-context';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import {
  SmartOrderAction,
  useSmartOrderReducer,
} from './hooks/use-smart-order-reducer';
import { SmartOrderResults } from './smart-order-results';
import { SpeechWaveform } from '@/components/speech-waveform';
import { Text, useWindowDimensions, View } from 'react-native';
import { TextArea } from '@/components/text-area';
import { useGetAddToOrderDisabled } from '@/hooks/use-add-to-order-disabled';
import { useGetSmartOrderResults } from '@/hooks/use-get-smart-order';
import { useSpeechRecognition } from '@/hooks/use-speech-recognition';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

const LINE_HEIGHT = 24;
const INITIAL_NUMBER_OF_LINES = 8;
const MAX_NUMBER_OF_LINES = 12;
const MICROPHONE_BUTTON_SIZE = 40;

enum SmartOrderActionSheetRoute {
  Input = 'Input',
  Results = 'Results',
}

interface Props {
  onClose: () => void;
}

export const SmartOrderActionSheet: React.FC<Props> = ({ onClose }) => {
  const [styles, theme] = useStyles();
  const windowDimensions = useWindowDimensions();
  const { showErrorToast } = useToasts();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const { getSmartOrderResults } = useGetSmartOrderResults();

  const [textInputHeight, setTextInputHeight] = useState(
    LINE_HEIGHT * INITIAL_NUMBER_OF_LINES,
  );
  const getIsItemDisabled = useGetAddToOrderDisabled();

  const [state, dispatch] = useSmartOrderReducer(getIsItemDisabled);
  const [smartOrderLoading, setSmartOrderLoading] = useState(false);
  // Setting 0 initial width crashes the app
  const [waveformWidth, setWaveformWidth] = useState(1);
  const cursorPositionRef = useRef(0);

  const requestedItems = state.requestedItems;
  const trimmedRequestedItems = requestedItems.trim();

  const updateSmartOrderText = useCallback(
    (text: string) =>
      dispatch({
        payload: {
          requestedItems: text,
        },
        type: SmartOrderAction.SET_REQUESTED_ITEMS,
      }),
    [dispatch],
  );

  const {
    canListen,
    isListening,
    speechVolumeStream,
    startRecognition,
    stopRecognition,
  } = useSpeechRecognition({
    cursorPositionRef,
    inputValue: requestedItems,
    setInputValue: updateSmartOrderText,
  });

  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.9);
  const resultsRouteStyle = [styles.flex, { maxHeight: actionSheetMaxHeight }];

  return (
    <CombinedActionSheet
      animateTransitions
      avoidKeyboard
      contentStyle={styles.base}
      initialRoute={SmartOrderActionSheetRoute.Input}
      onClose={onClose}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case SmartOrderActionSheetRoute.Input:
            return (
              <ActionSheetContent
                actions={[i18n.t('build_order')]}
                disabledIndices={!trimmedRequestedItems ? [0] : undefined}
                loadingActionIndex={smartOrderLoading ? 0 : undefined}
                message={
                  <View style={styles.base}>
                    <View style={styles.inputContent}>
                      <Text style={TYPOGRAPHY.h2}>
                        {i18n.t('smart_order.title')}
                      </Text>
                      <Text style={TYPOGRAPHY.p}>
                        {i18n.t('smart_order.description')}
                      </Text>
                    </View>
                    <TextArea
                      autoFocus
                      containerStyle={[
                        styles.textAreaContainer,
                        {
                          height: Math.max(
                            Math.min(
                              // Add additional line height to always show last line
                              textInputHeight + LINE_HEIGHT,
                              LINE_HEIGHT * MAX_NUMBER_OF_LINES,
                            ),
                            LINE_HEIGHT * INITIAL_NUMBER_OF_LINES,
                          ),
                        },
                      ]}
                      onChangeText={updateSmartOrderText}
                      onContentSizeChange={(e) =>
                        setTextInputHeight(e.nativeEvent.contentSize.height)
                      }
                      onSelectionChange={(event) => {
                        cursorPositionRef.current =
                          event.nativeEvent.selection.start;
                      }}
                      onTouchEnd={stopRecognition}
                      placeholder={i18n.t('smart_order.placeholder')}
                      showDoneButtonAccessoryView
                      style={styles.textArea}
                      value={requestedItems}
                    />
                    {canListen ? (
                      <View style={styles.microphoneRow}>
                        <View
                          onLayout={(e) =>
                            setWaveformWidth(e.nativeEvent.layout.width)
                          }
                          style={styles.flex}
                        >
                          {isListening ? (
                            <SpeechWaveform
                              height={MICROPHONE_BUTTON_SIZE}
                              speechVolumeStream={speechVolumeStream}
                              width={waveformWidth}
                            />
                          ) : null}
                        </View>
                        <Button
                          Icon={MicIcon}
                          iconProps={{ fill: theme.colors.transparent }}
                          onPress={
                            isListening ? stopRecognition : startRecognition
                          }
                          rounded
                          size="small"
                          style={[
                            styles.microphoneButton,
                            isListening
                              ? styles.microphoneActive
                              : styles.microphoneInactive,
                          ]}
                          testID="microphone-button"
                          trackingData={{
                            name: 'smart_order.speech_to_text',
                            properties: { active: !isListening },
                          }}
                        />
                      </View>
                    ) : null}
                  </View>
                }
                onAction={async () => {
                  if (isListening) {
                    stopRecognition();
                  }

                  Analytics.track('smart_order.build_order_button.press', {
                    requestedItems: trimmedRequestedItems,
                    restaurantUUID,
                    supplierUUID,
                  });

                  setSmartOrderLoading(true);
                  try {
                    const { data } = await getSmartOrderResults(
                      requestedItems,
                      true,
                    );
                    dispatch({
                      payload: {
                        results: data.getSmartOrderResults,
                      },
                      type: SmartOrderAction.GROUP_INITIAL_RESULTS,
                    });
                    jumpTo(SmartOrderActionSheetRoute.Results);
                  } catch {
                    showErrorToast();
                  }

                  setSmartOrderLoading(false);
                }}
              />
            );
          case SmartOrderActionSheetRoute.Results:
            return (
              <GestureHandlerRootView style={styles.cover}>
                <ItemDetailContextProvider>
                  <View style={styles.results}>
                    <SmartOrderResults
                      dispatch={dispatch}
                      getSmartOrderResults={getSmartOrderResults}
                      goBack={() => jumpTo(SmartOrderActionSheetRoute.Input)}
                      onClose={onClose}
                      state={state}
                    />
                  </View>
                </ItemDetailContextProvider>
              </GestureHandlerRootView>
            );
          default:
            return null;
        }
      }}
      routes={{
        [SmartOrderActionSheetRoute.Input]: {
          previousKey: null,
        },
        [SmartOrderActionSheetRoute.Results]: {
          contentStyle: resultsRouteStyle,
          modalStyle: resultsRouteStyle,
          previousKey: SmartOrderActionSheetRoute.Input,
          showSmallLayoutBackButton: true,
          smallLayoutBackButtonStyle: styles.backButton,
        },
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  backButton: {
    marginLeft: 0,
  },
  base: {
    justifyContent: 'space-between',
  },
  // Allow gesture handling to appear over entire screen instead of action sheet
  cover: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  flex: {
    flex: 1,
  },
  inputContent: {
    gap: 4,
    marginBottom: 24,
  },
  microphoneActive: {
    backgroundColor: theme.colors.recording,
  },
  microphoneButton: {
    alignSelf: 'flex-end',
    height: MICROPHONE_BUTTON_SIZE,
    width: MICROPHONE_BUTTON_SIZE,
  },
  microphoneInactive: {
    backgroundColor: theme.colors.accentLight,
  },
  microphoneRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 8,
  },
  results: {
    flex: 1,
    // Back button
    marginTop: 40,
    padding: SCREEN_PADDING,
  },
  textArea: {
    lineHeight: LINE_HEIGHT,
  },
  textAreaContainer: {
    borderColor: theme.colors.stroke,
  },
}));
